"use client";
import { useEffect, useState } from "react";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import {
  DEFAULT_SEARCH_SIMILAR_SPACES_LINK,
  buildSimilarSpacesUrl,
} from "~/components/listings/utils";
import { useLocaleData } from "~/i18n/IntlProvider";
import { IListing } from "~/types/api-types";
import { cloudinaryImageLoader } from "~/utils/image-loaders";
import { localizeLink } from "~/utils/url.utils";

export default function NotFound() {
  const [similarSpacesLink, setSimilarSpacesLink] = useState(
    DEFAULT_SEARCH_SIMILAR_SPACES_LINK,
  );
  const { urlPrefixWithSlash } = useLocaleData();

  const page = usePathname();
  const listingId = page
    ?.split("/")
    .find((_, i, arr) => arr[i - 1] === "listings");
  const isListingPage = page?.includes("/listings/") && listingId;

  useEffect(() => {
    (async () => {
      if (!isListingPage) {
        return;
      }
      const listingResponse = await fetch(`/v1/listings/${listingId}`);
      if (!listingResponse.ok) {
        return;
      }

      const listing = (await listingResponse.json()) as IListing;
      setSimilarSpacesLink(
        buildSimilarSpacesUrl({
          city: listing.city,
          state: listing.state,
          useType: listing.use_type,
        }),
      );
    })();
  }, [listingId, isListingPage]);

  return (
    <section className="ps-container-fixed ps-container-lg-margin tw-relative tw-flex tw-text-center tw-h-screen">
      <div className="ps-row">
        <div className="col-xs-12 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-base-white tw-z-10">
          <p className="tw-headline-1-m md:tw-headline-2-m">
            <FormattedMessage
              id="not_found_page.dead_end"
              defaultMessage="This is a dead end."
            />
          </p>
          <p className="tw-headline-6-m md:tw-display-2-b tw-mt-16 md:tw-mt-32">
            <FormattedMessage
              id="not_found_page.perfect_space"
              defaultMessage="Let's get you to the perfect space."
            />
          </p>
          <Button
            className="tw-mt-32 md:tw-mt-48"
            appearance="white"
            href={localizeLink({
              link: isListingPage ? similarSpacesLink : "/",
              urlPrefix: urlPrefixWithSlash,
            })}
          >
            {isListingPage ? (
              <FormattedMessage
                id="not_found_page.search_button"
                defaultMessage="Search similar spaces"
              />
            ) : (
              <FormattedMessage
                id="not_found_page.search_button_generic"
                defaultMessage="Search spaces"
              />
            )}
          </Button>
          {isListingPage && (
            <Button
              className="tw-mt-20 md:tw-mt-24"
              appearance="white"
              variant="underline-m"
              href={localizeLink({ link: "/", urlPrefix: urlPrefixWithSlash })}
            >
              <FormattedMessage
                id="not_found_page.go_home"
                defaultMessage="Go to Home"
              />
            </Button>
          )}
        </div>
      </div>
      <Image
        className="tw-object-cover tw-w-full"
        src="v1725545702/hky7uhntcthcyiiwjbst_vctdtv"
        fill
        loader={cloudinaryImageLoader}
        alt="404"
        priority
      />
    </section>
  );
}
